/**
 * Role Derivation Module
 * Determines user roles (superadmin) from token claims, email lists, and domains
 */

import type { Config } from './config';

interface User {
  email?: string;
  [key: string]: unknown;
}

interface TokenPayload {
  'https://lotlingo.com/isSuperUser'?: boolean | string;
  isSuperUser?: boolean | string;
  email_verified?: boolean;
  [key: string]: unknown;
}

interface RoleEnv {
  superAdminEmails?: string;
  superAdminDomain?: string;
}

declare global {
  interface Window {
    LOT_SUPERADMIN_EMAILS?: string;
    LOT_SUPERADMIN_DOMAIN?: string;
  }
}

/**
 * Derive superadmin status from token claims and fallback configuration
 * @param {object} user - Candidate user object with email, sub, etc.
 * @param {object} payload - ID token payload with claims
 * @param {object} env - Environment config (emails, domain)
 * @returns {boolean} - True if user should be elevated to superadmin
 */
export function deriveSuperadmin(user: User, payload: TokenPayload, env: RoleEnv = {}): boolean {
  // Priority 1: Token custom claim (Auth0 Action should set this)
  const customSuper = payload?.['https://lotlingo.com/isSuperUser'];
  let claimSuper: boolean | string | undefined;
  if (typeof payload?.isSuperUser === 'boolean') {
    claimSuper = payload.isSuperUser;
  } else if (typeof payload?.isSuperUser === 'string') {
    claimSuper = payload.isSuperUser;
  } else if (typeof customSuper === 'boolean' || typeof customSuper === 'string') {
    claimSuper = customSuper;
  }

  if (claimSuper) {
    return true;
  }

  // Priority 2: Explicit email list override
  const superAdminEmailConfig = (env.superAdminEmails || '').toString();
  const superAdminEmails = superAdminEmailConfig
    .split(',')
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);

  const emailLower = (user?.email || '').toLowerCase();
  if (emailLower && superAdminEmails.includes(emailLower)) {
    return true;
  }

  // Priority 3: Domain fallback (requires verified email)
  const superAdminDomain = (env.superAdminDomain || '').toString().trim().toLowerCase();
  if (superAdminDomain && emailLower.endsWith(`@${superAdminDomain}`)) {
    const emailVerified = payload?.email_verified !== false; // treat undefined as true (legacy tokens)
    if (emailVerified) {
      return true;
    }
  }

  return false;
}

/**
 * Resolve environment configuration from runtime or build-time sources
 * @returns {object} - { superAdminEmails, superAdminDomain }
 */
export function resolveRoleEnv(): RoleEnv {
  // Try runtime config first, then window globals, then build-time env
  return {
    superAdminEmails:
      (window.__LOTLINGO_CONFIG__ as any)?.superadmin?.emails ||
      window.LOT_SUPERADMIN_EMAILS ||
      import.meta?.env?.VITE_PORTAL_SUPERADMIN_EMAILS ||
      '',
    superAdminDomain:
      (window.__LOTLINGO_CONFIG__ as any)?.superadmin?.domain ||
      window.LOT_SUPERADMIN_DOMAIN ||
      import.meta?.env?.VITE_PORTAL_SUPERADMIN_DOMAIN ||
      '',
  };
}
